import React, { useEffect, useState } from "react";
import { IconButton, Box, Tooltip } from "@mui/material";
import {
  Visibility as PreviewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  CurrencyExchange as RefundIcon,
  Restore as ReverseIcon,
} from "@mui/icons-material";
import { downloadFile, theme } from "../../../utils";
import { PaymentRecord } from "../../../types";
import { ConfirmationModal, Modal, PreviewModal } from "../../common";
import Loading from "../../common/loading/Loading";
import { useFetchPreviewPaymentReceiptQuery } from "../../../api";
import { PaymentMethod, PaymentStatus, TransactionType } from "../../../enums";
import { RefundForm } from "./RefundForm";

type PaymentsActionIconsProps = {
  paymentRecord: PaymentRecord;
  handleRefund: (
    data: Pick<
      PaymentRecord,
      "id" | "orgId" | "accountId" | "invoiceNumber"
    > & { refundAmount: any },
  ) => void;
  handleReverse: (payment: PaymentRecord) => void;
};

export const PaymentsActionIcons: React.FC<PaymentsActionIconsProps> = ({
  paymentRecord,
  handleRefund,
  handleReverse,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [linkToPdfData, setLinkToPdfData] = useState<string>("");
  const [fetchOnClick, setFetchOnClick] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const pdfFileName = `${paymentRecord.referenceNumber}.pdf`;
  const isAchPayment = paymentRecord.paymentMethod === PaymentMethod.Ach;

  const isAchTransactionInitiated =
    isAchPayment && paymentRecord.status === PaymentStatus.TransactionInitiated;

  const handleOpenPreviewModal = (event: any) => {
    event.stopPropagation();
    setFetchOnClick(true);
    setIsPreviewModalOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setFetchOnClick(false);
  };

  const downloadPdf = (event: any) => {
    event.stopPropagation();
    if (linkToPdfData) downloadFile(linkToPdfData, pdfFileName);
    else {
      setFetchOnClick(true);
      setIsDownloading(true);
    }
  };

  const fetchPaymentReceiptPreviewQuery = useFetchPreviewPaymentReceiptQuery(
    paymentRecord.orgId,
    paymentRecord.accountId,
    paymentRecord.invoiceNumber,
    paymentRecord.referenceNumber,
    paymentRecord.transactionType === TransactionType.Refund ? true : false,
    { enabled: fetchOnClick },
  );

  useEffect(() => {
    if (
      fetchPaymentReceiptPreviewQuery.isSuccess &&
      fetchPaymentReceiptPreviewQuery.data
    ) {
      const dataToArrayBuffer = new Uint8Array(
        fetchPaymentReceiptPreviewQuery.data,
      );
      const linkToPdf = URL.createObjectURL(
        new Blob([dataToArrayBuffer], {
          type: "application/pdf",
        }),
      );
      setLinkToPdfData(linkToPdf);
      if (isDownloading) {
        downloadFile(linkToPdf, pdfFileName);
        setIsDownloading(false);
      }
    }
  }, [
    fetchPaymentReceiptPreviewQuery.isSuccess,
    fetchPaymentReceiptPreviewQuery.data,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flow",
          width: "50px",
        }}
      >
        <Tooltip title="Preview">
          <IconButton
            onClick={handleOpenPreviewModal}
            disabled={isAchTransactionInitiated}
          >
            <PreviewIcon
              fontSize="small"
              sx={{
                ...(!isAchTransactionInitiated && {
                  color: theme.palette.primary.main,
                }),
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            onClick={downloadPdf}
            disabled={isAchTransactionInitiated}
          >
            <DownloadIcon
              fontSize="small"
              sx={{
                ...(!isAchTransactionInitiated && {
                  color: theme.palette.primary.main,
                }),
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Refund">
          <IconButton
            onClick={() => {
              setIsRefundModalOpen(true);
            }}
            disabled={isAchTransactionInitiated || !paymentRecord.allowReturn}
          >
            <RefundIcon
              fontSize="small"
              sx={{
                ...(paymentRecord.allowReturn &&
                  !isAchTransactionInitiated && {
                    color: theme.palette.primary.main,
                  }),
              }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reverse Payment">
          <IconButton
            onClick={() => {
              setIsConfirmationModalOpen(true);
            }}
            disabled={
              isAchPayment
                ? !isAchTransactionInitiated
                : !paymentRecord.allowReversal
            }
          >
            <ReverseIcon
              fontSize="large"
              sx={{
                ...((isAchPayment
                  ? isAchTransactionInitiated
                  : paymentRecord.allowReversal) && {
                  color: theme.palette.primary.main,
                }),
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onClose={() => {
          setIsConfirmationModalOpen(false);
        }}
        onConfirm={() => {
          setIsConfirmationModalOpen(false);
          handleReverse(paymentRecord);
        }}
        icon={
          <ReverseIcon
            fontSize="medium"
            sx={{
              color: theme.palette.primary.main,
            }}
          />
        }
        title={`Reverse Payment`}
        message={`Are you sure you want reverse this payment?`}
      />

      <Modal
        open={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        title={"Refund"}
        content={
          <RefundForm
            id={"refundForm"}
            handleEdit={(data) => {
              handleRefund(data);
              setIsRefundModalOpen(false);
            }}
            defaultValues={paymentRecord}
          ></RefundForm>
        }
        form={"refundForm"}
        primaryButtonText="Refund"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsRefundModalOpen(false)}
        sx={{
          modal: {
            width: { lg: "30%", xs: "80%", sm: "50%" },
          },
        }}
      />

      {isPreviewModalOpen && (
        <PreviewModal
          open={isPreviewModalOpen}
          linkToPdfData={linkToPdfData}
          loader={fetchPaymentReceiptPreviewQuery.isLoading}
          onClose={handleClosePreviewModal}
          fileName={pdfFileName}
        />
      )}
      {isDownloading && (
        <Modal
          open={isDownloading}
          onClose={() => setIsDownloading(false)}
          title={""}
          content={
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "30px",
                textAlign: "center",
                gap: 2,
              }}
            >
              <Loading message="Downloading..."></Loading>
            </Box>
          }
          sx={{
            modal: {
              width: { lg: "40%", xs: "80%", sm: "80%" },
            },
          }}
        />
      )}
    </>
  );
};
